'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import styles from '@/app/[locale]/lodges/lodges.module.css'

interface FlipCardProps {
  lodge: {
    id: string
    name: string
    slug: string
    subtitle: string
    location: string
    heroImage: string
    features: { title: string }[]
    comingSoon?: boolean
    comingSoonLabel?: string
    soldOut?: boolean
  }
  index: number
}

export default function FlipCard({ lodge, index }: FlipCardProps) {
  const [isFlipped, setIsFlipped] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const tUi = useTranslations('lodgesPage.ui')

  // Detect touch device on mount
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(hover: none)').matches
      )
    }

    checkTouch()

    // Re-check on resize (for devices that can switch modes)
    window.addEventListener('resize', checkTouch)
    return () => window.removeEventListener('resize', checkTouch)
  }, [])

  const handleClick = () => {
    if (isTouchDevice) {
      setIsFlipped(!isFlipped)
    }
  }

  const amenities = lodge.features.map((f) => f.title)
  const number = String(index + 1).padStart(2, '0')

  return (
    <div
      className={styles.flipCardWrapper}
      onClick={handleClick}
      role={isTouchDevice ? 'button' : undefined}
      tabIndex={isTouchDevice ? 0 : undefined}
      aria-label={isTouchDevice ? `${tUi('tapToExplore')} ${lodge.name}` : undefined}
      onKeyDown={(e) => {
        if (isTouchDevice && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          setIsFlipped(!isFlipped)
        }
      }}
    >
      <div
        className={styles.flipCard}
        style={isFlipped ? { transform: 'rotateY(180deg)' } : undefined}
      >
        {/* Front Face */}
        <div className={styles.flipCardFront}>
          <div
            className={styles.flipCardImage}
            style={{ backgroundImage: `url(${lodge.heroImage})` }}
          ></div>
          <div className={styles.flipCardOverlay}></div>
          {(lodge.comingSoon || lodge.soldOut) && (
            <span className={styles.comingSoonBadge}>
              {lodge.comingSoonLabel || tUi('comingSoon')}
            </span>
          )}
          <div className={styles.flipCardContentFront}>
            <span className={styles.flipNumber}>{number}</span>
            <h2 className={styles.flipTitle}>{lodge.name}</h2>
            <p className={styles.flipRegion}>{lodge.location}</p>
            <span className={styles.flipHint}>
              {isTouchDevice ? `${tUi('tapToExplore')} →` : `${tUi('hoverToExplore')} →`}
            </span>
          </div>
        </div>

        {/* Back Face */}
        <div className={styles.flipCardBack}>
          <div className={styles.flipBackContent}>
            <span className={styles.flipBackNumber}>{number}</span>
            <h2 className={styles.flipBackTitle}>{lodge.name}</h2>
            <p className={styles.flipBackDescription}>
              {lodge.subtitle}
            </p>
            {amenities.length > 0 && (
              <div className={styles.flipAmenities}>
                {amenities.map((amenity) => (
                  <span key={amenity} className={styles.amenityBadge}>
                    {amenity}
                  </span>
                ))}
              </div>
            )}
            <Link
              href={`/lodges/${lodge.slug}`}
              className={styles.flipCta}
              aria-label={`${tUi('viewDetails')} ${lodge.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              {tUi('viewDetails')}
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 9h12M9 3l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
